/**
 * Whole Life LOC - Skeleton
 *
 * Supplies initial/default values for the Whole Life benefits grid.
 * Used when creating new coverage or normalizing empty API payloads.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  BaseWholeLifeBenefitEmployeeBenefitMaximumType,
  RfpWholeLifeBenefitDependentBenefitType,
} from "@platform/openapi-types";

import {
  BenefitPlanTypeEnum,
  CoverageTypeEnum,
  FundingTypeEnum,
  PlanOptionNameEnum,
  RfpBenefitPlan,
  RfpCoverage,
} from "../../types/rfp.api.types";

import type { WholeLifeBenefitsGrid } from "./types";

/**
 * Whole Life skeleton
 *
 * IMPORTANT: Benefits are in GRID FORMAT (flat fields), not API format (nested objects).
 * This allows proper merging with data transformed from API via transformFromApi.
 */

export function getWholeLifeSkeleton(): Partial<WholeLifeBenefitsGrid> {
  return {
    currentCarrierName: "",

    // Employee Benefit Amount
    employeeBenefitAmountType: undefined,
    employeeBenefitAmountAmount: 1,

    // Employee Benefit Maximum
    employeeBenefitMaximumType:
      BaseWholeLifeBenefitEmployeeBenefitMaximumType.dollarAmount,
    employeeBenefitMaximumDollarAmount: 1,

    // Guarantee Issue Amount
    guaranteeIssueAmount: 1,

    // Spouse Benefit Amount
    spouseBenefitAmountType:
      RfpWholeLifeBenefitDependentBenefitType.notIncluded,
    spouseBenefitAmountAmount: 1,

    // Spouse Benefit Maximum
    spouseBenefitMaximumType:
      RfpWholeLifeBenefitDependentBenefitType.notIncluded,
    spouseBenefitMaximumAmount: 1,

    // Child Benefit Amount
    childBenefitAmountType: RfpWholeLifeBenefitDependentBenefitType.notIncluded,
    childBenefitAmountAmount: 1,

    // Child Benefit Maximum
    childBenefitMaximumType:
      RfpWholeLifeBenefitDependentBenefitType.notIncluded,
    childBenefitMaximumAmount: 1,

    // Other fields
    portability: undefined,
    coverageEffectiveDate: undefined,
    endowsAge: 1,

    // Extended (non-v5) fields — CD-2072/CD-390
    longTermCare: undefined,
  };
}

/**
 * Gets the Whole Life coverage skeleton (for skeleton registry integration)
 * Returns a full coverage object with a default benefit plan
 */
export function getWholeLifeCoverageSkeleton(): Partial<RfpCoverage> {
  return {
    type: CoverageTypeEnum.WHOLE_LIFE,
    status: "quoted",
    notes: "",
    benefitPlans: [
      {
        identifier: "whole-life-plan-1",
        name: "Whole Life Plan",
        numberOfLives: 0,
        type: BenefitPlanTypeEnum.NOT_APPLICABLE,
        policyPeriod: BaseBenefitPlanPolicyPeriod.newBusiness,
        fundingType: FundingTypeEnum.FULLY_INSURED,
        participationRequirements: "",
        rateGuarantee: undefined,
        rateCap: "",
        optionName: PlanOptionNameEnum.NOT_APPLICABLE,
        benefitClassAvailability: [],
        rateSchedule: [],
        benefits: getWholeLifeSkeleton(),
      } as RfpBenefitPlan,
    ],
  };
}
